import { useUser } from "@dashboard/auth";
import { hasPermission } from "@dashboard/auth/misc";
import { WindowTitle } from "@dashboard/components/WindowTitle";
import {
  PermissionEnum,
  useNewsletterSubscribersQuery,
  useStaffNotificationRecipientCreateMutation,
  useStaffNotificationRecipientDeleteMutation,
  useStaffNotificationRecipientsQuery,
  useStaffNotificationRecipientUpdateMutation,
  useUpdateMetadataMutation,
} from "@dashboard/graphql";
import useListSettings from "@dashboard/hooks/useListSettings";
import useNotifier from "@dashboard/hooks/useNotifier";
import { usePaginationReset } from "@dashboard/hooks/usePaginationReset";
import usePaginator, {
  createPaginationState,
  PaginatorContext,
} from "@dashboard/hooks/usePaginator";
import { commonMessages } from "@dashboard/intl";
import { ListViews } from "@dashboard/types";
import { mapEdgesToItems } from "@dashboard/utils/maps";
import { FC } from "react";
import { useIntl } from "react-intl";

import { messages } from "../components/NewsletterListPage/messages";
import { NewsletterListPage } from "../components/NewsletterListPage/NewsletterListPage";
import { NewsletterSubscriber, StaffNotificationRecipient } from "../types";
import { newsletterListUrl, NewsletterListUrlQueryParams } from "../urls";

// Set by the storefront newsletter form; "false" removes the customer from the list.
const NEWSLETTER_SUBSCRIBED_KEY = "newsletter_subscribed";

interface NewsletterListViewProps {
  params: NewsletterListUrlQueryParams;
}

export const NewsletterListView: FC<NewsletterListViewProps> = ({ params }) => {
  const intl = useIntl();
  const notify = useNotifier();
  const { user } = useUser();
  const { updateListSettings, settings } = useListSettings(ListViews.NEWSLETTER_LIST);

  const canManageSettings = !!user && !!hasPermission(PermissionEnum.MANAGE_SETTINGS, user);

  usePaginationReset(newsletterListUrl, params, settings.rowNumber);

  const paginationState = createPaginationState(settings.rowNumber, params);

  const {
    data,
    loading,
    refetch: refetchSubscribers,
  } = useNewsletterSubscribersQuery({
    displayLoader: true,
    variables: paginationState,
  });

  const { data: recipientsData, refetch: refetchRecipients } = useStaffNotificationRecipientsQuery({
    displayLoader: true,
    skip: !canManageSettings,
  });

  const notifyError = () =>
    notify({ status: "error", text: intl.formatMessage(commonMessages.somethingWentWrong) });

  const [createRecipient, createRecipientOpts] = useStaffNotificationRecipientCreateMutation({});
  const [updateRecipient] = useStaffNotificationRecipientUpdateMutation({});
  const [deleteRecipient, deleteRecipientOpts] = useStaffNotificationRecipientDeleteMutation({});
  const [updateMetadata, updateMetadataOpts] = useUpdateMetadataMutation({});

  const handleAddRecipient = async (input: { email: string; active: boolean }) => {
    const result = await createRecipient({ variables: { input } });
    const errors = result.data?.staffNotificationRecipientCreate?.errors ?? [];

    if (errors.length > 0) {
      notifyError();

      return false;
    }

    notify({ status: "success", text: intl.formatMessage(messages.recipientAdded) });
    await refetchRecipients();

    return true;
  };

  const handleToggleRecipient = async (recipient: StaffNotificationRecipient) => {
    const result = await updateRecipient({
      variables: { id: recipient.id, input: { active: !recipient.active } },
    });
    const errors = result.data?.staffNotificationRecipientUpdate?.errors ?? [];

    if (errors.length > 0) {
      notifyError();

      return;
    }

    notify({ status: "success", text: intl.formatMessage(messages.recipientUpdated) });
    await refetchRecipients();
  };

  const handleDeleteRecipient = async (recipient: StaffNotificationRecipient) => {
    const result = await deleteRecipient({ variables: { id: recipient.id } });
    const errors = result.data?.staffNotificationRecipientDelete?.errors ?? [];

    if (errors.length > 0) {
      notifyError();

      return false;
    }

    notify({ status: "success", text: intl.formatMessage(messages.recipientDeleted) });
    await refetchRecipients();

    return true;
  };

  const handleUnsubscribe = async (subscriber: NewsletterSubscriber) => {
    const result = await updateMetadata({
      variables: {
        id: subscriber.id,
        input: [{ key: NEWSLETTER_SUBSCRIBED_KEY, value: "false" }],
        keysToDelete: [],
      },
    });
    const errors = result.data?.updateMetadata?.errors ?? [];

    if (errors.length > 0) {
      notifyError();

      return false;
    }

    notify({ status: "success", text: intl.formatMessage(messages.unsubscribed) });
    await refetchSubscribers();

    return true;
  };

  const paginationValues = usePaginator({
    pageInfo: data?.customers?.pageInfo,
    paginationState,
    queryString: params,
  });

  const subscribers = loading ? undefined : (mapEdgesToItems(data?.customers) ?? []);
  const recipients = recipientsData?.shop?.staffNotificationRecipients ?? undefined;

  return (
    <PaginatorContext.Provider value={paginationValues}>
      <WindowTitle title={intl.formatMessage(messages.title)} />
      <NewsletterListPage
        subscribers={subscribers}
        recipients={canManageSettings ? recipients : undefined}
        canManageSettings={canManageSettings}
        disabled={loading}
        settings={settings}
        onUpdateListSettings={updateListSettings}
        addRecipientState={createRecipientOpts.status}
        deleteRecipientState={deleteRecipientOpts.status}
        unsubscribeState={updateMetadataOpts.status}
        onAddRecipient={handleAddRecipient}
        onToggleRecipient={handleToggleRecipient}
        onDeleteRecipient={handleDeleteRecipient}
        onUnsubscribe={handleUnsubscribe}
      />
    </PaginatorContext.Provider>
  );
};

export default NewsletterListView;
